'use client';

import React from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';
import { CATEGORIES } from '@/lib/content';

// Data-driven sequence, update this array to change the order
const START_HERE_SEQUENCE = [
  {
    order: 1,
    slug: 'what-security-is-actually-trying-to-protect',
    title: 'What security is actually trying to protect',
    description: 'Confidentiality, integrity and availability. Three words that explain most of what security is for.',
    category: 'security-fundamentals',
    available: false,
  },
  {
    order: 2,
    slug: 'risk-is-not-the-same-as-severity',
    title: 'Risk is not the same as severity',
    description: 'Severity tells you how bad a vulnerability is in theory. Risk asks what it means for you.',
    category: 'security-fundamentals',
    available: false,
  },
  {
    order: 3,
    slug: 'defence-in-depth',
    title: 'Defence in depth',
    description: 'The principle behind layered security. No single control should be the last line of defence.',
    category: 'security-fundamentals',
    available: false,
  },
  {
    order: 4,
    slug: 'least-privilege',
    title: 'Least privilege',
    description: 'Give people and systems only what they need. Nothing more.',
    category: 'identity-and-access',
    available: true,
  },
  {
    order: 5,
    slug: 'identity-is-the-new-perimeter',
    title: 'Identity is the new perimeter',
    description: 'When work happens everywhere, the network edge is no longer the security boundary.',
    category: 'identity-and-access',
    available: true,
  },
  {
    order: 6,
    slug: 'why-mfa-is-not-all-the-same',
    title: 'Why MFA is not all the same',
    description: 'All MFA is better than none. Some MFA is much better than others.',
    category: 'identity-and-access',
    available: true,
  },
  {
    order: 7,
    slug: 'what-zero-trust-actually-means',
    title: 'What zero trust actually means',
    description: 'Zero trust is not a product. It is an architectural principle.',
    category: 'architecture-and-resilience',
    available: true,
  },
  {
    order: 8,
    slug: 'shared-responsibility-in-cloud',
    title: 'Shared responsibility in cloud',
    description: 'Moving to cloud does not transfer your security responsibilities. It redistributes them.',
    category: 'cloud-security',
    available: true,
  },
  {
    order: 9,
    slug: 'cloud-security-is-mostly-an-identity-problem',
    title: 'Why cloud security is mostly an identity problem',
    description: 'In cloud environments, identity and configuration often define the boundary.',
    category: 'cloud-security',
    available: false,
  },
  {
    order: 10,
    slug: 'what-is-an-attack-surface',
    title: 'What an attack surface really is',
    description: 'You cannot protect what you cannot see. Attack surface is everything an attacker might reach.',
    category: 'security-fundamentals',
    available: true,
  },
  {
    order: 11,
    slug: 'severity-isnt-risk',
    title: 'Why vulnerabilities are not equally important',
    description: 'Sorting by CVSS score alone is not risk-based prioritisation.',
    category: 'vulnerability-management',
    available: true,
  },
  {
    order: 12,
    slug: 'what-lateral-movement-means',
    title: 'What lateral movement means',
    description: 'Getting in is often the easy part. Lateral movement is how attackers get to what they actually want.',
    category: 'detection-and-soc',
    available: true,
  },
  {
    order: 13,
    slug: 'blast-radius',
    title: 'What blast radius means',
    description: 'You cannot always prevent the explosion. You can limit how far it reaches.',
    category: 'architecture-and-resilience',
    available: true,
  },
  {
    order: 14,
    slug: 'detection-versus-prevention',
    title: 'Detection versus prevention',
    description: 'Prevention is valuable. Detection is essential. Both have limits.',
    category: 'detection-and-soc',
    available: false,
  },
  {
    order: 15,
    slug: 'why-backups-must-be-tested',
    title: 'Why backups must be tested',
    description: 'An untested backup is a hope, not a recovery plan.',
    category: 'architecture-and-resilience',
    available: false,
  },
  {
    order: 16,
    slug: 'secure-by-design',
    title: 'What secure by design means',
    description: 'Security bolted on afterwards is fragile and expensive. Security designed in from the start is structural.',
    category: 'application-security',
    available: true,
  },
  {
    order: 17,
    slug: 'what-prompt-injection-means',
    title: 'What prompt injection means',
    description: 'When AI processes untrusted content, that content can become an instruction.',
    category: 'ai-security',
    available: false,
  },
  {
    order: 18,
    slug: 'ai-agent-is-more-than-a-chatbot',
    title: 'Why AI agents change the security model',
    description: 'The security model changes when AI can act, not just answer.',
    category: 'ai-security',
    available: true,
  },
  {
    order: 19,
    slug: 'software-supply-chain-risk',
    title: 'Software supply chain risk',
    description: 'The code you did not write can still compromise you.',
    category: 'application-security',
    available: false,
  },
  {
    order: 20,
    slug: 'one-mistake-shouldnt-be-enough',
    title: 'One mistake should not be enough',
    description: 'People make predictable mistakes. Good security limits what happens next.',
    category: 'human-factors',
    available: true,
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  'security-fundamentals': 'var(--accent)',
  'identity-and-access': 'var(--color-identity)',
  'cloud-security': 'var(--accent)',
  'ai-security': 'var(--color-warning)',
  'application-security': 'var(--color-success)',
  'vulnerability-management': 'var(--color-danger)',
  'architecture-and-resilience': 'var(--accent)',
  'detection-and-soc': 'var(--accent)',
  'human-factors': 'var(--color-warning)',
};

export default function StartHerePage() {
  const availableCount = START_HERE_SEQUENCE.filter(i => i.available).length;

  return (
    <>
      <main className="pt-32 sm:pt-36 pb-20">
        {/* Page header */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mb-14">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Guided reading</p>
          <h1
            className="font-display font-bold text-primary mb-5"
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.0', letterSpacing: '-0.025em' }}
          >
            Start here
          </h1>
          <p className="text-lg text-foreground/80 leading-relaxed mb-4">
            Drawn2Security is a growing library. If you are new, this sequence gives you the most useful foundation.
          </p>
          <p className="text-base text-muted-foreground leading-relaxed">
            These twenty ideas are not the whole library. They are the ideas that make the rest easier to understand. Work through them in order, or jump to whatever is most relevant right now.
          </p>
          <div className="flex items-center gap-4 mt-6 p-4 rounded-xl bg-muted/50 border border-border">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircleIcon" size={20} variant="outline" className="text-success" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {availableCount} of 20 ideas published
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                The remaining ideas are in progress. This page updates as new sketches are published.
              </p>
            </div>
          </div>
        </div>

        {/* Sequence */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col">
            {START_HERE_SEQUENCE.map((item, index) => {
              const cat = CATEGORIES.find(c => c.id === item.category);
              const dotColor = CATEGORY_COLORS[item.category] || 'var(--accent)';

              return (
                <div key={item.slug} className="start-here-item group">
                  {/* Number */}
                  <div className="start-here-number flex-shrink-0">
                    {item.available ? (
                      <span className="text-foreground">{item.order}</span>
                    ) : (
                      <span className="text-muted-foreground/50">{item.order}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        {cat && (
                          <span
                            className="text-xs font-semibold uppercase tracking-widest mb-1.5 block"
                            style={{ color: dotColor }}
                          >
                            {cat.name}
                          </span>
                        )}
                        <h3
                          className={`font-display font-semibold text-base leading-snug start-here-title transition-colors duration-200 ${
                            item.available ? 'text-foreground' : 'text-muted-foreground/50'
                          }`}
                          style={{ fontFamily: 'var(--font-display)' }}
                        >
                          {item.title}
                        </h3>
                        <p className={`text-sm leading-relaxed mt-1.5 ${item.available ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>
                          {item.description}
                        </p>
                      </div>

                      {item.available ? (
                        <Link
                          href={`/sketches/${item.slug}`}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:border-accent hover:text-accent transition-all duration-200 group/btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Read
                          <Icon name="ArrowRightIcon" size={13} variant="outline" className="group-hover/btn:translate-x-0.5 transition-transform duration-200" />
                        </Link>
                      ) : (
                        <span className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border bg-muted text-xs font-medium text-muted-foreground/50">
                          Coming soon
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* After sequence CTA */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-14">
          <div className="p-7 rounded-2xl border border-border bg-card text-center">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">What next?</p>
            <h3
              className="font-display font-bold text-foreground text-xl mb-3"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Ready to explore further?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-sm mx-auto">
              Once you have worked through the foundation, browse by category or use search to follow whatever interests you most.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                href="/explore"
                className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all duration-200 group"
              >
                Browse all ideas
                <Icon name="ArrowRightIcon" size={14} variant="outline" className="group-hover:translate-x-0.5 transition-transform duration-200" />
              </Link>
              <Link
                href="/categories"
                className="inline-flex items-center gap-2 px-5 py-3 border border-border rounded-xl text-sm font-medium text-foreground hover:border-accent hover:text-accent transition-all duration-200"
              >
                Browse categories
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}